<template>
  <div class="flex flex-column gap-2">
    <div v-if="shift.pay_code" class="flex flex-column gap-2">
      <div class="flex gap-1 align-items-center">
        <a-tooltip class="w-fit" :title="shift.pay_code.description">
          <div class="flex gap-1 align-items-center">
            <a-tag class="m-0" color="#d47aff">
              {{ shift.pay_code.name }}
            </a-tag>
          </div>
        </a-tooltip>
        <a-tooltip
          class="w-fit"
          v-if="typeof shift.is_planned === 'boolean'"
          :title="shift.is_planned ? 'Planned' : 'Unexpected'"
        >
          <a-icon
            width="20"
            height="20"
            :style="{
              color: shift.is_planned ? 'var(--success-color)' : 'var(--warning-color)'
            }"
            :icon="shift.is_planned ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'"
          />
        </a-tooltip>
      </div>

      <a-tag v-show="shift.status === 'deleted'" :color="shiftStatus.color">{{ shiftStatus.title }}</a-tag>

      <a-tag v-show="shift.status !== 'deleted'" :color="paymentStatus.color">
        {{ paymentStatus.title }}
      </a-tag>
    </div>

    <div v-else-if="shift.external_payment_info" class="flex flex-column gap-2">
      <div v-if="shift.status !== 'completed'" class="flex gap-1 align-items-center">
        <a-tag :color="shiftStatus.color">{{ shiftStatus.title }}</a-tag>
        <a-tooltip
          v-if="shift.status === 'cancelled' && typeof shift.is_planned === 'boolean'"
          class="w-fit"
          :title="shift.is_planned ? 'Planned' : 'Unexpected'"
        >
          <a-icon
            width="20"
            height="20"
            :style="{
              color: shift.is_planned ? 'var(--success-color)' : 'var(--warning-color)'
            }"
            :icon="shift.is_planned ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'"
          />
        </a-tooltip>
      </div>

      <template
        v-else-if="
          shift.internal_payment_info.payment_status === 'not_paid' &&
          shift.external_payment_info.payment_status === 'not_paid'
        "
      >
        <payment-info-block
          :flags="notReadyFlags(shift.internal_payment_info)"
          :status="staffPaymentStatus.internal"
          @handleClick="$emit('handleClick', $event)"
        />
      </template>

      <div v-else>
        <payment-info-block
          label="Income:"
          :flags="notReadyFlags(shift.external_payment_info)"
          :status="staffPaymentStatus.external"
          @handleClick="$emit('handleClick', $event)"
        />
        <payment-info-block
          label="Expense:"
          :flags="notReadyFlags(shift.internal_payment_info)"
          :status="staffPaymentStatus.internal"
          @handleClick="$emit('handleClick', $event)"
        />
      </div>
    </div>

    <div v-else>
      <div v-if="shift.status !== 'completed'" class="flex gap-1 align-items-center">
        <a-tag :color="shiftStatus.color">{{ shiftStatus.title }}</a-tag>
        <a-tooltip
          v-if="shift.status === 'cancelled' && typeof shift.is_planned === 'boolean'"
          class="w-fit"
          :title="shift.is_planned ? 'Planned' : 'Unexpected'"
        >
          <a-icon
            width="20"
            height="20"
            :style="{
              color: shift.is_planned ? 'var(--success-color)' : 'var(--warning-color)'
            }"
            :icon="shift.is_planned ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'"
          />
        </a-tooltip>
      </div>

      <payment-info-block
        v-else
        :flags="notReadyFlags(shift.internal_payment_info)"
        :status="staffPaymentStatus.internal"
        @handleClick="$emit('handleClick', $event)"
      />
    </div>

    <div class="flex gap-2">
      <div
        class="flex gap-2"
        v-if="
          shift.internal_payment_info.flags &&
          (shift.internal_payment_info.flags.zero_rate ||
            shift.internal_payment_info.flags.training_period ||
            shift.internal_payment_info.flags.orientation_period)
        "
      >
        <a-tooltip v-if="shift.internal_payment_info.flags.zero_rate"
          ><template slot="title"> Zero Rate </template>
          <span
            class="wrapper"
            :style="{
              backgroundColor: 'var(--warning-color)'
            }"
          >
            <a-icon width="16" :style="{ color: '#fff' }" height="16" icon="mdi:numeric-0-circle-outline" />
          </span> </a-tooltip
        ><a-tooltip v-if="shift.internal_payment_info.flags.training_period"
          ><template slot="title"> Training Period </template>
          <span
            class="wrapper"
            :style="{
              backgroundColor: 'var(--success-color)'
            }"
          >
            <a-icon width="16" :style="{ color: '#fff' }" height="16" icon="mdi:book-open-variant-outline" />
          </span> </a-tooltip
        ><a-tooltip v-if="shift.internal_payment_info.flags.orientation_period"
          ><template slot="title"> Orientation Period </template>
          <span
            class="wrapper"
            :style="{
              backgroundColor: 'var(--info-color)'
            }"
          >
            <a-icon width="16" :style="{ color: '#fff' }" height="16" icon="mynaui:one-circle-solid" />
          </span>
        </a-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { Tag, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftTableStatusStaff from './table/shift-table-status-staff.vue'

const { mapGetters: shiftGetters } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-tag': Tag,
    'a-tooltip': Tooltip,
    'payment-info-block': ShiftTableStatusStaff
  },
  props: {
    shift: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {}
  },
  computed: {
    ...shiftGetters([
      'getShiftStatus',
      'getOrigin',
      'getShiftPaymentRequestStatus',
      'getReadyForPaymentStatus',
      'getNotReadyForPaymentFlag'
    ]),
    shiftStatus() {
      return this.getShiftStatus(this.shift.status)
    },

    paymentStatus() {
      return this.getPaymentStatus(this.shift.internal_payment_info)
    },

    staffPaymentStatus() {
      return {
        internal: this.getPaymentStatus(this.shift.internal_payment_info),
        external: this.getPaymentStatus(this.shift.external_payment_info)
      }
    }
  },
  methods: {
    getPaymentStatus(payment_info) {
      if (!payment_info) return undefined

      const { ready_for_pay_status, payment_status } = payment_info

      const isPaymentInProgress = payment_status !== 'not_paid'

      if (isPaymentInProgress) {
        return this.getShiftPaymentRequestStatus(payment_status)
      }

      return this.getReadyForPaymentStatus(ready_for_pay_status)
    },
    notReadyFlags(info) {
      if (['paid', 'partially_paid'].includes(info.payment_status) || info.ready_for_pay_status === 'not_ready') {
        return info.not_ready_to_pay_flags
          .map(flag => ({
            info: this.getNotReadyForPaymentFlag(flag),
            key: flag
          }))
          .filter(Boolean)
      } else {
        return []
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
